import React, { Fragment } from 'react'

import DOMPurify from 'dompurify'

import {
    PrincipleOfPublicityStandard,
    EditPreviewLinkStandard,
    QuestionRenderer,
    StepperStandard,
    StartApplicationButton,
    TextBody,
    QuestionGroup,
    ValidationErrorSummaryList,
    StepperButtonsStandard,
    StepperButtonsNoApiStandard,
} from 'optimized-react-component-library-xyz123'

/**
 * Huvudkomponenten som renderar hela PTS formuläret.
 * Hanterar alla vyer: start-sida, formulärsteg, preview-sida och tack-sida.
 *
 * Komponenten bygger upp själva strukturen för formuläret baserat på:
 *  - aktivt språk
 *  - nuvarande steg
 *  - vilka frågor som ska visas
 *  - om användaren befinner sig i input-läge eller preview-läge
 *
 * Logiken består huvudsakligen av fyra renderingstillstånd:
 *
 * 1. Start-sidan (activeStep === 0)
 *    Visar introduktionstext och en knapp för att starta ärendet.
 *
 * 2. Formulärläge (isFormMode)
 *    Visar ett specifikt steg i formuläret, kategorier, fråga-grupper,
 *    valideringssummering samt navigeringsknappar.
 *
 * 3. Preview-läge (showPreview === true)
 *    Visar en sammanställning av alla frågor och svar innan inskick.
 *    Varje steg kan redigeras genom "ändra"-länkar.
 *
 * 4. Tack-sidan (globalFormStatus === 'success')
 *    Visas när ärendet är inskickat och backend har svarat med success.
 *
 * Komponenten tar in alla handlers via props,
 * inklusive språkbyte, frågeändringar och steg-navigering.
 */

const PTSFormRenderer = ({
    globalLanguageState,
    globalActivatedLanguage,
    globalSteps,
    globalApplicationContent,
    globalFormStatus,
    globalThankYouPageContent,
    globalQuestions,
    showPreview = false,
    activeStep,
    currentStepQuestions = [],
    previewPageData = [],
    markQuestionAsDisplayed,
    validationErrorsList = [],
    startApplicationButtonHandler,
    handleQuestionInputChange,
    onSubmitHandler,
    nextStepButtonHandler,
    changeStepHandler,
    appUsesApi,
    appUsesResetButton,
}: any) => {
    const isThankYouPage = globalFormStatus === 'success' // Formuläret är inskickat och tack-sidan visas
    const isStartPage = activeStep === 0 // Första steget i formuläret (start-sidan)
    const isFormMode = !isThankYouPage && !isStartPage // Vanligt formulärläge (exempelvis steg 1-3 + preview-läge)
    const isInputMode = !showPreview // Exempelvis steg 1-3 (ej preview-läge eller start/tack-sida)
    const isPreviewMode = showPreview // Preview-läge

    return (
        <div>
            <div className="pts-form-step-header">
                {/* Denna h1 rubrik visas alltid i applikationen */}
                <h1 tabIndex={-1} id="pts-main-service-headline">
                    {isFormMode && currentStepQuestions.stepHeadline}
                    {isStartPage && globalApplicationContent.headline}
                    {isThankYouPage && globalThankYouPageContent?.headline}
                </h1>

                {isThankYouPage && <TextBody data={globalThankYouPageContent} />}
                {isStartPage && (
                    <>
                        <TextBody data={globalApplicationContent} />
                        <StartApplicationButton
                            onClick={startApplicationButtonHandler}
                            activatedLanguage={globalActivatedLanguage}
                        />
                    </>
                )}
                {isFormMode && (
                    <>
                        {currentStepQuestions.stepDescription &&
                            currentStepQuestions.stepDescription.length > 0 &&
                            (/<\/?[a-z][\s\S]*>/i.test(currentStepQuestions.stepDescription) ? (
                                <div dangerouslySetInnerHTML={{ __html: currentStepQuestions.stepDescription }} />
                            ) : (
                                <p>{currentStepQuestions.stepDescription}</p>
                            ))}
                        <StepperStandard activeStep={activeStep} arraySteps={globalSteps} />

                        <section
                            id="pts-error-summary"
                            className={validationErrorsList.length === 0 ? 'unsetMargin-Padding' : 'error-container'}
                        >
                            <ValidationErrorSummaryList
                                questions={globalQuestions}
                                validationErrorsList={validationErrorsList}
                                activatedLanguage={globalActivatedLanguage}
                            />
                        </section>
                    </>
                )}
            </div>

            {/* Formulärdelen - Inkluderar både input-läge och preview-läge */}
            {isFormMode && (
                <form id="pts-form-id" noValidate onSubmit={e => e.preventDefault()}>
                    {/* Visas endast i input-läge, ej preview-läge */}
                    {isInputMode &&
                        currentStepQuestions &&
                        currentStepQuestions.categories &&
                        currentStepQuestions.categories.map((category: any, index: number) => {
                            const categoryHeadlineId = `category-title-${index}`
                            const categoryDescriptionId = `category-description-${index}`
                            return (
                                <Fragment key={'categori-section' + index}>
                                    {category.visible && (
                                        <section>
                                            <h2 id={categoryHeadlineId}>{category.category}</h2>

                                            {category.categoryDescription && (
                                                <>
                                                    {/* Kontroll för att se om categoryDescription innehåller HTML-taggar */}
                                                    {category.categoryDescription.includes('<') &&
                                                    category.categoryDescription.includes('>') ? (
                                                        <div
                                                            id={categoryDescriptionId}
                                                            style={{ marginBottom: '1rem' }}
                                                            dangerouslySetInnerHTML={{
                                                                __html: DOMPurify.sanitize(
                                                                    category.categoryDescription
                                                                ),
                                                            }}
                                                        />
                                                    ) : (
                                                        <p id={categoryDescriptionId}>{category.categoryDescription}</p>
                                                    )}
                                                </>
                                            )}
                                            {category.questionGroups &&
                                                category.questionGroups.map((questionGroup: any, index: number) => {
                                                    return (
                                                        <Fragment key={'questionGroupIndex-' + index}>
                                                            <QuestionGroup
                                                                questions={questionGroup.questions}
                                                                wrapperElement={questionGroup.questionGroupType}
                                                                legendLabel={questionGroup.legend}
                                                                handleQuestionInputChange={handleQuestionInputChange}
                                                                activatedLanguage={globalActivatedLanguage}
                                                                showPreview={showPreview}
                                                                markQuestionAsDisplayed={markQuestionAsDisplayed}
                                                            />
                                                        </Fragment>
                                                    )
                                                })}
                                        </section>
                                    )}
                                </Fragment>
                            )
                        })}

                    {/* Visas endast i preview-läge, ej input-läge */}
                    {isPreviewMode && previewPageData && (
                        <>
                            <div className="pts-preview-page">
                                {previewPageData &&
                                    previewPageData.map((step: any, index: number) => {
                                        const stepPreviewHeadlineId = `category-preview-title-${index}`

                                        return (
                                            <Fragment key={'pts-preview' + index}>
                                                <section>
                                                    <div className="pts-preview-header">
                                                        <h2 id={stepPreviewHeadlineId}>
                                                            {step.step.shortNameInPreview}
                                                        </h2>
                                                        <EditPreviewLinkStandard
                                                            activatedLanguage={globalActivatedLanguage}
                                                            changeStepHandler={changeStepHandler}
                                                            step={step.step}
                                                        />
                                                    </div>
                                                    <dl>
                                                        {step.questions &&
                                                            step.questions.map((question: any, index: number) => {
                                                                return (
                                                                    <Fragment key={'pts-question' + index}>
                                                                        <QuestionRenderer
                                                                            question={question}
                                                                            handleQuestionInputChange={
                                                                                handleQuestionInputChange
                                                                            }
                                                                            activatedLanguage={globalActivatedLanguage}
                                                                            showPreview={true}
                                                                        />
                                                                    </Fragment>
                                                                )
                                                            })}{' '}
                                                    </dl>
                                                </section>
                                            </Fragment>
                                        )
                                    })}{' '}
                            </div>
                            <PrincipleOfPublicityStandard activatedLanguage={globalActivatedLanguage} />
                        </>
                    )}

                    {/* Knappar visas både i input-läge och preview-läge */}
                    {appUsesApi && (
                        <StepperButtonsStandard
                            globalLanguageState={globalLanguageState}
                            changeStepHandler={changeStepHandler}
                            nextStepButtonHandler={nextStepButtonHandler}
                            onSubmitHandler={onSubmitHandler}
                            activeStep={activeStep}
                            steps={globalSteps}
                        />
                    )}
                    {!appUsesApi && (
                        <StepperButtonsNoApiStandard
                            globalLanguageState={globalLanguageState}
                            changeStepHandler={changeStepHandler}
                            nextStepButtonHandler={nextStepButtonHandler}
                            activeStep={activeStep}
                            steps={globalSteps}
                            appUsesResetButton={appUsesResetButton}
                        />
                    )}
                </form>
            )}
        </div>
    )
}
export default PTSFormRenderer
